"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.getPublicKeyEncoded = getPublicKeyEncoded;
exports.getPublicKeyUnencoded = getPublicKeyUnencoded;
exports.getPrivateKeyFromWIF = getPrivateKeyFromWIF;
exports.getWIFFromPrivateKey = getWIFFromPrivateKey;
exports.getPublicKeyFromPrivateKey = getPublicKeyFromPrivateKey;
exports.getVerificationScriptFromPublicKey = getVerificationScriptFromPublicKey;
exports.getPublicKeyFromVerificationScript = getPublicKeyFromVerificationScript;
exports.getScriptHashFromVerificationScript = getScriptHashFromVerificationScript;
exports.getScriptHashFromPublicKey = getScriptHashFromPublicKey;
exports.getAddressFromScriptHash = getAddressFromScriptHash;
exports.getScriptHashFromAddress = getScriptHashFromAddress;
exports.generatePrivateKey = generatePrivateKey;
exports.getAddressVersion = getAddressVersion;
/**
 * Core methods for manipulating keys
 *
 * ```
 * NEP2 <=> WIF <=> Private => Public => ScriptHash <=> Address
 * ```
 *
 * Keys are arranged in order of derivation.
 * Arrows determine the direction.
 *
 * NEP2 methods are found within NEP2 standard.
 * All methods take in Big-Endian strings and return Big-Endian strings.
 */
const bs58_1 = __importDefault(require("bs58"));
const buffer_1 = require("buffer");
const consts_1 = require("../consts");
const u_1 = require("../u");
const sc_1 = require("../sc");
const curve = (0, u_1.getCurve)(u_1.EllipticCurvePreset.SECP256R1);
/**
 * Encodes a public key.
 * @param unencodedKey - unencoded public key
 * @returns encoded public key
 */
function getPublicKeyEncoded(unencodedKey) {
    const publicKeyArray = new Uint8Array((0, u_1.hexstring2ab)(unencodedKey));
    if (publicKeyArray[64] % 2 === 1) {
        return "03" + (0, u_1.ab2hexstring)(publicKeyArray.slice(1, 33));
    }
    else {
        return "02" + (0, u_1.ab2hexstring)(publicKeyArray.slice(1, 33));
    }
}
/**
 * Unencodes a public key.
 * @param publicKey - Encoded public key
 * @returns decoded public key
 */
function getPublicKeyUnencoded(publicKey) {
    return curve.decodePublicKey(publicKey);
}
/**
 * Converts a WIF to private key.
 */
function getPrivateKeyFromWIF(wif) {
    const assembledKeyWithChecksum = (0, u_1.ab2hexstring)(bs58_1.default.decode(wif));
    return assembledKeyWithChecksum.substr(2, 64);
}
/**
 * Converts a private key to WIF.
 */
function getWIFFromPrivateKey(privateKey) {
    const assembledKey = "80" + privateKey + "01";
    const hashed = (0, u_1.hash256)(assembledKey);
    const checksum = hashed.substr(0, 8); // first 4 bytes
    const assembledKeyWithChecksum = assembledKey + checksum;
    return bs58_1.default.encode((0, u_1.hexstring2ab)(assembledKeyWithChecksum));
}
/**
 * Converts a private key to public key.
 * @param privateKey - 64 bit private key
 * @param encode - returns the encoded form if true.
 */
function getPublicKeyFromPrivateKey(privateKey, encode = true) {
    return curve.getPublicKey(privateKey, encode);
}
/**
 * Converts a public key to verification script form.
 * VerificationScript serves a very niche purpose.
 * It is attached as part of the signature when signing a transaction.
 * Thus, the name 'scriptHash' instead of 'keyHash' is because we are hashing the verificationScript and not the PublicKey.
 */
function getVerificationScriptFromPublicKey(publicKey) {
    const sb = new sc_1.ScriptBuilder();
    return sb
        .emit(sc_1.OpCode.PUSHDATA1, "21" + publicKey)
        .emitSysCall(sc_1.InteropServiceCode.SYSTEM_CRYPTO_CHECKSIG)
        .build();
}
/**
 * Extracts the public key from the verification script. This only works for single key accounts.
 * @param script - hexstring
 */
function getPublicKeyFromVerificationScript(script) {
    const ops = sc_1.OpToken.fromScript(script);
    const sysCallToken = ops.pop();
    if (sysCallToken === undefined ||
        sysCallToken.code !== sc_1.OpCode.SYSCALL ||
        (sysCallToken.params ?? "") !== sc_1.InteropServiceCode.SYSTEM_CRYPTO_CHECKSIG) {
        throw new Error("script is not a single key account.");
    }
    const publicKeyToken = ops[0];
    if (publicKeyToken.code !== sc_1.OpCode.PUSHDATA1 || !publicKeyToken.params) {
        throw new Error("cannot find public key");
    }
    return publicKeyToken.params;
}
/**
 * Converts a verification script to scripthash.
 *
 * @param verificationScript - hexstring
 */
function getScriptHashFromVerificationScript(verificationScript) {
    return (0, u_1.reverseHex)((0, u_1.hash160)(verificationScript));
}
/**
 * Converts a public key to scripthash.
 */
function getScriptHashFromPublicKey(publicKey) {
    // if unencoded
    if (publicKey.substring(0, 2) === "04") {
        publicKey = getPublicKeyEncoded(publicKey);
    }
    const verificationScript = getVerificationScriptFromPublicKey(publicKey);
    return (0, u_1.reverseHex)((0, u_1.hash160)(verificationScript));
}
/**
 * Converts a scripthash to address.
 */
function getAddressFromScriptHash(scriptHash, addressVersion = consts_1.DEFAULT_ADDRESS_VERSION) {
    scriptHash = (0, u_1.reverseHex)(scriptHash);
    const addressVersionHex = addressVersion.toString(16);
    const shaChecksum = (0, u_1.hash256)(addressVersionHex + scriptHash).substr(0, 8);
    return bs58_1.default.encode(buffer_1.Buffer.from(addressVersionHex + scriptHash + shaChecksum, "hex"));
}
/**
 * Converts an address to scripthash.
 */
function getScriptHashFromAddress(address) {
    const hash = (0, u_1.ab2hexstring)(bs58_1.default.decode(address));
    return (0, u_1.reverseHex)(hash.substr(2, 40));
}
/**
 * Generates a random private key.
 */
function generatePrivateKey() {
    return (0, u_1.ab2hexstring)((0, u_1.generateRandomArray)(32));
}
/**
 * Extracts the address version from a given correct NEO address.
 * @param address - A NEO address string.
 * @returns Address version
 */
function getAddressVersion(address) {
    return bs58_1.default.decode(address)[0];
}
